import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import config from 'constants/config'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import ContactContext from 'contexts/contactContext'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import shouldRenderTimely, { getTimelyLink } from 'lib/timely/timely'
import type { ComponentProps, MouseEventHandler } from 'react'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import useInfiniteConciergeDetails from 'loyalty/hooks/useInfiniteConciergeDetails'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'

interface Props extends Pick<ComponentProps<typeof DropdownList>, 'anchorRef' | 'open' | 'onClose'> {
  currentRegionCode: string
  currentRegionName: string
  onItemClick: () => void
  supportAssistantEnabled: boolean
}

function NavigationHeaderDropdown(props: Props) {
  const intl = useIntl()
  const {
    currentRegionCode,
    currentRegionName,
    onItemClick,
    supportAssistantEnabled,
    open,
    anchorRef,
    onClose,
  } = props
  const currentContact = useContext(ContactContext)
  const isInfiniteMember = useAppSelector(state => getLoyaltyIsInfiniteAccountTier(state))
  const conciergeDetails = useInfiniteConciergeDetails()
  const localContact = currentContact.local
  const internationalContact = currentContact.international
  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)

  const showTimelyPopup = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    onItemClick()
    window.open(getTimelyLink(), 'popup', 'width=600,fullscreen=no,location=no,menubar=no,toolbar=no')
  }, [onItemClick])

  const localContactTitle = intl.formatMessage({
    id: 'VuL2uw26xFn_p37H50vj',
    defaultMessage: 'Local Sales & Support',
    description: 'Title for local sales and support contact in dropdown',
  })

  return (
    <DropdownList
      size="S"
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      triggerRef={anchorRef}
    >
      {isInfiniteMember && conciergeDetails && (
        <ClickableListItem
          size="medium"
          aria-label={conciergeDetails.title}
          href={conciergeDetails.phoneHref}
          startIcon={<LinePhoneIcon />}
          title={conciergeDetails.title}
          onClick={onItemClick}
          rel="nofollow"
          subtitle={conciergeDetails.phoneNumber}
        />
      )}
      {!NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) && (
        <ClickableListItem
          size="medium"
          aria-label={intl.formatMessage(
            {
              id: 'BIiLPF1RtZf-kQFh2Brl',
              defaultMessage: 'Local Sales & Support contact - {regionName}',
              description: 'Aria label for local sales and support contact in dropdown',
            },
            { regionName: currentRegionName },
          )}
          href={`tel:${localContact.number}`}
          startIcon={<LinePhoneIcon />}
          title={localContactTitle}
          onClick={onItemClick}
          rel="nofollow"
          subtitle={localContact.humanReadable}
        />
      )}
      {supportAssistantEnabled && <ClickableListItem
        size="medium"
        aria-label={intl.formatMessage(
          {
            id: 'C8vdLh6rTjNkSGh23y0e',
            defaultMessage: 'Luxury Escapes Support Assistant - {regionName}',
            description: 'Aria label for support assistant contact in dropdown',
          },
          { regionName: currentRegionName },
        )}
        to="/support"
        startIcon={<LineCommentsIcon />}
        title={intl.formatMessage({
          id: 'SlwEhmhhpmwLT-7ALpid',
          defaultMessage: 'Chat now',
          description: 'Title for chat support option in contact dropdown on the form',
        })}
        onClick={onItemClick}
        rel="nofollow"
        subtitle={intl.formatMessage({
          id: 'BzN1IBsaypgUKpsLj5Ki',
          defaultMessage: 'Luxury Escapes Support Assistant',
          description: 'Subtitle for chat support showing assistant name in contact dropdown',
        })}
      />}
      {shouldRenderWhatsApp && <ClickableListItem
        size="medium"
        title={intl.formatMessage({
          id: '3VFHvLZzeQOrAnh6VT_y',
          defaultMessage: 'WhatsApp',
          description: 'Title for WhatsApp contact option in navigation header dropdown',
        })}
        aria-label={intl.formatMessage(
          {
            id: 'YYMMVLnKN77OOsJIq3fO',
            defaultMessage: 'WhatsApp - {regionName}',
            description: 'Aria label for WhatsApp contact in navigation header dropdown',
          },
          { regionName: currentRegionName },
        )}
        startIcon={<img
          src={getSVGImageUrl('whatsapp-logo')}
          width="24"
          height="24"
          alt={intl.formatMessage({
            id: '9bf7j0uVJtgdzxhsatWj',
            defaultMessage: 'WhatsApp',
            description: 'Alt text for WhatsApp logo in navigation header dropdown',
          })}
        />}
        onClick={onItemClick}
        href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
        target="_blank"
        rel="noopener"
      />}
      {shouldRenderTimely(currentRegionCode) && <ClickableListItem
        size="medium"
        title={intl.formatMessage({
          id: 'bi_sQ_dhSjxVsHG8WwgF',
          defaultMessage: 'Book store appointment',
          description: 'Title for book store appointment option in contact dropdown',
        })}
        aria-label={intl.formatMessage(
          {
            id: '7WwLh2VZyIfOQB1OdHRQ',
            defaultMessage: 'Timely - {regionName}',
            description: 'Aria label for store appointment booking in contact dropdown',
          },
          { regionName: currentRegionName },
        )}
        startIcon={<img src={getSVGImageUrl('logo-LE')} width="24" height="24" alt={intl.formatMessage({ id: 'ZxMrCgY7j1Vg_fpYGeA3', defaultMessage: 'Support', description: 'Alt text for support logo icon in store appointment booking option' })} />}
        onClick={showTimelyPopup}
        href={getTimelyLink()}
        target="popup"
        rel="noopener"
      />}
      <ClickableListItem
        size="medium"
        startIcon={<LinePhoneIcon />}
        aria-label={intl.formatMessage({
          id: 'Ehfc60I_OkMUU-xoRz2W',
          defaultMessage: 'International Support contact',
          description: 'Aria label for international support contact in dropdown',
        })}
        href={`tel:${internationalContact.number}`}
        title={intl.formatMessage({
          id: 'J1RshtIDp-xxbqpfd6o-',
          defaultMessage: 'International Support',
          description: 'Title for international support contact in dropdown',
        })}
        onClick={onItemClick}
        rel="nofollow"
        subtitle={internationalContact.humanReadable}
      />
    </DropdownList>
  )
}

const mapStateToProps = (state: App.State) => ({
  currentRegionCode: state.geo.currentRegionCode,
  currentRegionName: state.geo.currentRegionName,
  supportAssistantEnabled: isSupportAssistantEnabled(state),
})

export default connect(mapStateToProps)(NavigationHeaderDropdown)
